/**
 * Opening a candidate's resume, outside the jobs state machine.
 *
 * "View resume" did nothing on staging (Eula, 16 Oct 2026). Two reasons, both
 * fixed here:
 *
 * 1. Pop-up blockers. The page asked the machine for a presigned link and
 *    opened the new tab in an effect once the link arrived. By then the click
 *    was over, so the browser no longer counted the tab as the user's own
 *    action: Safari blocks that outright, and Chrome does once the request
 *    takes long enough. Now the tab is opened during the click itself, shows
 *    "Opening the resume…", and is pointed at the file when the link arrives.
 *
 * 2. Dropped and dead-ended requests. Like the interview and offer events
 *    (useCandidateInterview, useCandidateOfferEvents), the link was a state of
 *    `jobsFSM`, which silently drops an event sent while it is busy and has an
 *    ERROR state that accepts no events at all. One failed request anywhere on
 *    the page left the button dead until a reload, with nothing on screen.
 *    Here every click makes its own request, and a failure closes the waiting
 *    tab and says why (`onError`).
 *
 * The opened tab has its opener cleared before it is used, so the document it
 * shows cannot reach back into this page (what `noopener` gave before).
 */

import { useCallback } from 'react';
import type { AxiosError } from 'axios';
import axios from '../../../axiosConfig';

export const RESUME_FALLBACK_MESSAGE =
  "Couldn't open the resume. Please try again.";

/** The server's own reason when it gives one ("Resume not available for candidate …"), else a plain retry line. */
export const resumeErrorMessage = (error: unknown): string => {
  const data = (error as AxiosError<{ message?: unknown }>)?.response?.data;
  return typeof data?.message === 'string' && data.message.trim()
    ? data.message
    : RESUME_FALLBACK_MESSAGE;
};

/** A blank tab, opened while the click is still being handled. Null when the browser refused it. */
const openWaitingTab = (): Window | null => {
  const tab = window.open('', '_blank');
  if (!tab) return null;
  tab.opener = null;
  try {
    tab.document.title = 'Opening resume…';
    tab.document.body.textContent = 'Opening the resume…';
  } catch {
    // Writing into the blank tab is a courtesy; a browser may not allow it.
  }
  return tab;
};

export const useCandidateResume = (onError: (message: string) => void) => {
  /** Call it from the click handler itself, so the tab opens as part of the click. */
  const open = useCallback(
    (jobId: number, candidateId: number) => {
      const tab = openWaitingTab();
      axios
        .get<{ url?: string }>(
          `/api/jobs/${jobId}/candidates/${candidateId}/resume`,
        )
        .then(({ data }) => {
          if (!data?.url) throw new Error('No resume link in the response');
          if (tab && !tab.closed) {
            tab.location.href = data.url;
          } else {
            // The browser refused the waiting tab, or the person closed it:
            // one plain attempt, which a blocker may still stop.
            window.open(data.url, '_blank', 'noopener,noreferrer');
          }
        })
        .catch((error: unknown) => {
          if (tab && !tab.closed) tab.close();
          onError(resumeErrorMessage(error));
        });
    },
    [onError],
  );

  return { open };
};
